import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';

type I18nLabel = Record<Language, string>;

// ============================================
// Selection Types - Internal Types do Backend
// ============================================

// Selection (base - generic select with options)
interface SelectionProps extends Omit<ComponentPropsWithoutRef<'select'>, 'children'> {
    options: string[];
    placeholder?: string;
    i18nPlaceholder?: I18nLabel;
}

export const Selection = forwardRef<HTMLSelectElement, SelectionProps>(({ options, placeholder, i18nPlaceholder, className = '', ...props }, ref) => {
    const lang = useLanguage();
    const displayPlaceholder = i18nPlaceholder ? i18nPlaceholder[lang] : placeholder;
    return (
        <select {...props} ref={ref} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${className}`}>
            {displayPlaceholder && (
                <option value="" disabled>
                    {displayPlaceholder}
                </option>
            )}
            {options.map((option) => (
                <option key={option} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
});
Selection.displayName = 'Selection';

// Selection.Country (select from country codes)
interface SelectionCountryProps extends Omit<ComponentPropsWithoutRef<'select'>, 'children'> {
    placeholder?: string;
    i18nPlaceholder?: I18nLabel;
}

const COUNTRIES = ['BRA', 'USA', 'CAN', 'GBR', 'FRA', 'DEU', 'ESP', 'ITA', 'PRT', 'ARG', 'MEX', 'CHL', 'URY', 'PRY', 'BOL', 'PER', 'COL', 'VEN', 'ECU', 'JPN'];

export const SelectionCountry = forwardRef<HTMLSelectElement, SelectionCountryProps>(({ placeholder = 'Selecione o país', i18nPlaceholder, className = '', ...props }, ref) => {
    const lang = useLanguage();
    const displayPlaceholder = i18nPlaceholder ? i18nPlaceholder[lang] : placeholder;
    return (
        <select {...props} ref={ref} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${className}`}>
            <option value="" disabled>
                {displayPlaceholder}
            </option>
            {COUNTRIES.map((country) => (
                <option key={country} value={country}>
                    {country}
                </option>
            ))}
        </select>
    );
});
SelectionCountry.displayName = 'SelectionCountry';

// Selection.Color (color names + hex input)
interface SelectionColorProps extends Omit<ComponentPropsWithoutRef<'select'>, 'children'> {
    placeholder?: string;
    i18nPlaceholder?: I18nLabel;
}

const COLOR_OPTIONS = ['black', 'blue', 'brown', 'cyan', 'gray', 'green', 'magenta', 'orange', 'pink', 'purple', 'red', 'white', 'yellow'];

export const SelectionColor = forwardRef<HTMLSelectElement, SelectionColorProps>(({ placeholder = 'Selecione uma cor', i18nPlaceholder, className = '', ...props }, ref) => {
    const lang = useLanguage();
    const displayPlaceholder = i18nPlaceholder ? i18nPlaceholder[lang] : placeholder;
    return (
        <select {...props} ref={ref} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${className}`}>
            <option value="" disabled>
                {displayPlaceholder}
            </option>
            {COLOR_OPTIONS.map((color) => (
                <option key={color} value={color}>
                    {color}
                </option>
            ))}
        </select>
    );
});
SelectionColor.displayName = 'SelectionColor';

// Selection.NaturalPersonDocumentName (CPF, etc)
interface SelectionNaturalPersonDocumentNameProps extends Omit<ComponentPropsWithoutRef<'select'>, 'children'> {
    placeholder?: string;
    i18nPlaceholder?: I18nLabel;
}

const NATURAL_PERSON_DOCUMENTS = ['CPF', 'DNI', 'SSN', 'NIE', 'NIF'];

export const SelectionNaturalPersonDocumentName = forwardRef<HTMLSelectElement, SelectionNaturalPersonDocumentNameProps>(({ placeholder = 'Tipo de documento', i18nPlaceholder, className = '', ...props }, ref) => {
    const lang = useLanguage();
    const displayPlaceholder = i18nPlaceholder ? i18nPlaceholder[lang] : placeholder;
    return (
        <select {...props} ref={ref} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${className}`}>
            <option value="" disabled>
                {displayPlaceholder}
            </option>
            {NATURAL_PERSON_DOCUMENTS.map((doc) => (
                <option key={doc} value={doc}>
                    {doc}
                </option>
            ))}
        </select>
    );
});
SelectionNaturalPersonDocumentName.displayName = 'SelectionNaturalPersonDocumentName';

// Selection.LegalPersonDocumentName (CNPJ, etc)
interface SelectionLegalPersonDocumentNameProps extends Omit<ComponentPropsWithoutRef<'select'>, 'children'> {
    placeholder?: string;
    i18nPlaceholder?: I18nLabel;
}

const LEGAL_PERSON_DOCUMENTS = ['CNPJ', 'EIN', 'CIF'];

export const SelectionLegalPersonDocumentName = forwardRef<HTMLSelectElement, SelectionLegalPersonDocumentNameProps>(({ placeholder = 'Tipo de documento', i18nPlaceholder, className = '', ...props }, ref) => {
    const lang = useLanguage();
    const displayPlaceholder = i18nPlaceholder ? i18nPlaceholder[lang] : placeholder;
    return (
        <select {...props} ref={ref} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${className}`}>
            <option value="" disabled>
                {displayPlaceholder}
            </option>
            {LEGAL_PERSON_DOCUMENTS.map((doc) => (
                <option key={doc} value={doc}>
                    {doc}
                </option>
            ))}
        </select>
    );
});
SelectionLegalPersonDocumentName.displayName = 'SelectionLegalPersonDocumentName';

// Selection.CustomOptions (dynamic options)
interface SelectionCustomOptionsProps extends Omit<ComponentPropsWithoutRef<'select'>, 'children'> {
    options: string[];
    placeholder?: string;
    i18nPlaceholder?: I18nLabel;
}

export const SelectionCustomOptions = forwardRef<HTMLSelectElement, SelectionCustomOptionsProps>(({ options, placeholder = 'Selecione uma opção', i18nPlaceholder, className = '', ...props }, ref) => {
    const lang = useLanguage();
    const displayPlaceholder = i18nPlaceholder ? i18nPlaceholder[lang] : placeholder;
    return (
        <select {...props} ref={ref} className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm ${className}`}>
            <option value="" disabled>
                {displayPlaceholder}
            </option>
            {options.map((option) => (
                <option key={option} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
});
SelectionCustomOptions.displayName = 'SelectionCustomOptions';
